import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./css/Place.css";
import {
    updateDoc,
    increment,
    setDoc,
    deleteDoc,
    serverTimestamp,
    onSnapshot,
    writeBatch
} from "firebase/firestore";

import { useAuth } from "../context/AuthContext";

export default function Place({ placeId: propPlaceId, onClose }) {
    const { maybeHandle } = useParams();
    const navigate = useNavigate();

    const isModal = !!propPlaceId;

    const [resolvedPlaceId, setResolvedPlaceId] = useState(
        propPlaceId || null
    );
    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();

    const [views, setViews] = useState(0);
    const [likesCount, setLikesCount] = useState(0);
    const [savesCount, setSavesCount] = useState(0);

    const [liked, setLiked] = useState(false);
    const [saved, setSaved] = useState(false);

    /* --------------------------------------------------
       1️⃣ Resolve slug → placeId (ONLY in page mode)
    -------------------------------------------------- */
    useEffect(() => {
        if (propPlaceId) return; // modal mode
        if (!maybeHandle) {
            setLoading(false);
            return;
        }

        let mounted = true;

        const resolveSlug = async () => {
            try {
                const slugSnap = await getDoc(doc(db, "slugs", maybeHandle));

                if (!mounted) return;

                if (slugSnap.exists()) {
                    setResolvedPlaceId(slugSnap.data().placeId);
                } else {
                    setLoading(false);
                }
            } catch (err) {
                console.error("Failed to resolve slug:", err);
                if (mounted) setLoading(false);
            }
        };

        resolveSlug();
        return () => (mounted = false);
    }, [propPlaceId, maybeHandle]);

    /* --------------------------------------------------
       2️⃣ Load place using resolved placeId
    -------------------------------------------------- */
    useEffect(() => {
        if (!resolvedPlaceId) return;

        let mounted = true;

        const loadPlace = async () => {
            try {
                const snap = await getDoc(doc(db, "places", resolvedPlaceId));

                if (!mounted) return;

                if (snap.exists()) {
                    setPlace(snap.data());
                }
            } catch (err) {
                console.error("Failed to load place:", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadPlace();
        return () => (mounted = false);
    }, [resolvedPlaceId]);

    /* --------------------------------------------------
       3️⃣ Modal behavior (ONLY in modal mode)
    -------------------------------------------------- */
    useEffect(() => {
        if (!isModal) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", onKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = "";
        };
    }, [isModal, onClose]);

    useEffect(() => {
        if (!resolvedPlaceId) return;

        const ref = doc(db, "places", resolvedPlaceId);

        const unsub = onSnapshot(ref, (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setViews(data.views || 0);
                setLikesCount(data.likesCount || 0);
                setSavesCount(data.savesCount || 0);
            }
        });

        return () => unsub();
    }, [resolvedPlaceId]);

    useEffect(() => {
        if (!resolvedPlaceId) return;

        const key = `place_viewed_${resolvedPlaceId}`;

        if (!localStorage.getItem(key)) {
            updateDoc(doc(db, "places", resolvedPlaceId), {
                views: increment(1),
            }).catch(console.error);

            localStorage.setItem(key, "true");
        }
    }, [resolvedPlaceId]);

    useEffect(() => {
        if (!currentUser || !resolvedPlaceId) {
            setLiked(false);
            setSaved(false);
            return;
        }

        async function checkStatus() {
            const likeSnap = await getDoc(
                doc(db, "places", resolvedPlaceId, "likes", currentUser.uid)
            );

            const saveSnap = await getDoc(
                doc(db, "places", resolvedPlaceId, "saves", currentUser.uid)
            );

            setLiked(likeSnap.exists());
            setSaved(saveSnap.exists());
        }

        checkStatus();
    }, [currentUser, resolvedPlaceId]);

    const handleLike = async () => {
        if (!currentUser) return;

        const placeRef = doc(db, "places", resolvedPlaceId);
        const likeRef = doc(db, "places", resolvedPlaceId, "likes", currentUser.uid);

        if (liked) {
            await deleteDoc(likeRef);
            await updateDoc(placeRef, {
                likesCount: increment(-1),
            });
            setLiked(false);
        } else {
            await setDoc(likeRef, {
                uid: currentUser.uid,
                createdAt: serverTimestamp(),
            });

            await updateDoc(placeRef, {
                likesCount: increment(1),
            });

            setLiked(true);
        }
    };

    const handleSave = async () => {
        if (!currentUser || !resolvedPlaceId) return;

        const uid = currentUser.uid;

        const placeRef = doc(db, "places", resolvedPlaceId);
        const saveRef = doc(db, "places", resolvedPlaceId, "saves", uid);

        const userBookmarkRef = doc(
            db,
            "users",
            uid,
            "bookmarks",
            `place_${resolvedPlaceId}`
        );

        const batch = writeBatch(db);

        if (saved) {
            // 🔴 REMOVE BOOKMARK
            batch.delete(saveRef);
            batch.delete(userBookmarkRef);
            batch.update(placeRef, {
                savesCount: increment(-1),
            });

            setSaved(false);
        } else {
            // 🟢 ADD BOOKMARK
            batch.set(saveRef, {
                uid,
                createdAt: serverTimestamp(),
            });

            batch.set(userBookmarkRef, {
                type: "place",
                refId: resolvedPlaceId,
                slug: place.slug,
                title: place.name,
                image: place.images?.[0] || null,
                city: place.city || null,
                country: place.country || null,
                createdAt: serverTimestamp(),
            });

            batch.update(placeRef, {
                savesCount: increment(1),
            });

            setSaved(true);
        }

        await batch.commit();
    };

    /* --------------------------------------------------
       4️⃣ Loading / 404
    -------------------------------------------------- */
    if (loading) return null;
    if (!place) return <div className="place-not-found">Place not found</div>;

    const imageSection = (
        <div className="place-col place-col-image">
            {Array.isArray(place.images) && place.images.length > 0 && (
                <ImageCarousel images={place.images} />
            )}
        </div>
    );

    /* --------------------------------------------------
       5️⃣ Image Carousel
    -------------------------------------------------- */
    function ImageCarousel({ images }) {
        const [index, setIndex] = useState(0);

        const prev = () =>
            setIndex((i) => (i === 0 ? images.length - 1 : i - 1));

        const next = () =>
            setIndex((i) => (i === images.length - 1 ? 0 : i + 1));

        return (
            <div className="carousel">
                <img
                    src={images[index]}
                    alt={`${place.name} ${index + 1}`}
                    loading="lazy"
                />

                {images.length > 1 && (
                    <>
                        <button
                            className="carousel-btn prev"
                            onClick={prev}
                            aria-label="Previous image"
                        >
                            ‹
                        </button>

                        <button
                            className="carousel-btn next"
                            onClick={next}
                            aria-label="Next image"
                        >
                            ›
                        </button>

                        <div className="carousel-dots">
                            {images.map((_, i) => (
                                <span
                                    key={i}
                                    className={`dot ${i === index ? "active" : ""}`}
                                    onClick={() => setIndex(i)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }


    const contentSection = (
        <div className="place-col place-col-content">

            <div className="place-top-bar">
                <button
                    className="guide-back-link"
                    onClick={() => navigate(-1)}
                >
                    ← Go Back
                </button>

                <div className="place-actions">
                    <div className="place-stat">
                        👁 {views}
                    </div>

                    <button
                        className={`place-btn ${liked ? "active" : ""}`}
                        onClick={handleLike}
                    >
                        ❤️ {likesCount}
                    </button>

                    <button
                        className={`place-btn ${saved ? "active" : ""}`}
                        onClick={handleSave}
                    >
                        🔖 {savesCount}
                    </button>

                    {isModal && (
                        <button
                            className="place-close-btn"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {place.type && (
                <span className="place-type">
                    {place.type}
                </span>
            )}

            <h2 className="place-title">{place.name}</h2>

            {(place.city || place.country) && (
                <p className="place-location">
                    {place.city}
                    {place.city && place.country ? ", " : ""}
                    {place.country}
                </p>
            )}

            {place.shortDescription && (
                <p className="place-description">
                    {place.shortDescription}
                </p>
            )}

            <div className="place-meta">
                {place.bestTimeToVisit && (
                    <div>
                        <strong>Best time</strong>
                        <span>{place.bestTimeToVisit}</span>
                    </div>
                )}
                {place.recommendedTime && (
                    <div>
                        <strong>Recommended stay</strong>
                        <span>{place.recommendedTime}</span>
                    </div>
                )}
                {place.entryFee && (
                    <div>
                        <strong>Entry fee</strong>
                        <span>{place.entryFee}</span>
                    </div>
                )}
            </div>

            {Array.isArray(place.tips) && place.tips.length > 0 && (
                <section className="place-tips">
                    <h4>Travel tips</h4>
                    <ul>
                        {place.tips.map((tip, i) => (
                            <li key={i}>{tip}</li>
                        ))}
                    </ul>
                </section>
            )}

            {isModal && (
                <div className="modal-actions">
                    <button
                        className="btn-save"
                        onClick={() => navigate(`/${place.slug}`)}
                    >
                        Open full page →
                    </button>
                </div>
            )}
        </div>
    );

    /* --------------------------------------------------
       7️⃣ Page Mode
    -------------------------------------------------- */
    if (!isModal) {
        return (
            <div className="place-page-wrapper gallery-layout">

                {Array.isArray(place.images) && (
                    <div className={`place-gallery count-${place.images.length}`}>
                        {place.images.length === 0 && (
                            <div className="gallery-empty">
                                No photos available
                            </div>
                        )}

                        {place.images.slice(0, 5).map((img, i) => (
                            <div key={i} className="gallery-item">
                                <img src={img} alt={`${place.name} ${i + 1}`} />

                                {i === 4 && place.images.length > 5 && (
                                    <div className="gallery-more">
                                        +{place.images.length - 5} more
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}


                <div className="place-page-content">
                    {contentSection}
                </div>

            </div>
        );
    }

    /* --------------------------------------------------
       8️⃣ Modal Mode
    -------------------------------------------------- */
    if (isModal) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div
                    className="modal-box place-modal two-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {imageSection}
                    {contentSection}
                </div>
            </div>
        );
    }

}
